import React from 'react';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import {
	EmailShare,
	FacebookShare,
	LinkedinShare,
	TwitterShare,
} from '@app/components/UI/ShareButtons/ShareButtons';
import { ICON_LINK, TEXT_LINK } from '@app/components/Pages/Resources/FamilyResources/FRUtils';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import ModalVideo from '@app/components/UI/ModalVideo/ModalVideo';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { ContentEntity } from '@app/objects/ContentTopic';

interface VideosProps {
	searchQuery: string | undefined;
}

const Videos: React.FC<VideosProps> = ({ searchQuery }) => {
	const [modalVisible, setModalVisible] = React.useState(false);
	const [videoSrc, setVideoSrc] = React.useState('');
	const [videoIndex, setVideoIndex] = React.useState(0);
	const [loadingSSR, setLoadingSSR] = React.useState<boolean>(true);

	const context = useItemsProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		setLoadingSSR(false);
	}, []);

	React.useEffect(() => {
		handleChange({
			text: searchQuery || '',
			offset: 0,
		});
	}, [searchQuery]);

	const formatShareVideo = (videoUrl: string) => {
		if (videoUrl.indexOf('youtube.com') >= 0) {
			return videoUrl.replace('embed/', 'watch?v=');
		}

		return videoUrl;
	};

	return (
		<>
			<div className="blog-page__content blog-page__videos">
				{!loadingSSR && !loading && items.map((item, index) => (
					<article key={index} className={`article article-video-${index}`}>
						<div className="article__info">
							<h6 className="article__caption">{item.title}</h6>
							<p className="article__text">{item.smallText}</p>
							<div className="article__addition">
								<span
									onClick={() => {
										setModalVisible(true);
										setVideoSrc(item.url);
										setVideoIndex(index);
									}}
									className="article__more btn-text"
								>
									{TEXT_LINK[item.contentType]}
									{' '}
									<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
								</span>
								<div className="share-icons">
									<p>Share This Video</p>
									<FacebookShare url={formatShareVideo(item.url)} />
									<LinkedinShare url={formatShareVideo(item.url)} />
									<TwitterShare url={formatShareVideo(item.url)} />
									<EmailShare url={formatShareVideo(item.url)} />
								</div>
							</div>
						</div>
						<div className="article__photo">
							<CustomAvatar src={item.avatar} errorAvatar={imagePlaceholder} maxWidth={300} maxHeight={300} />
						</div>
					</article>
				))}

				{!loadingSSR && !loading && !items.length && (
					<div className="faq-block__dummy">
						Woof! We were not able to find a match. Please try another search.
					</div>
				)}

				{(loadingSSR || loading) && <BounceLoader />}
				<div className="text-center">
					<LoadMoreButton
						className="btn btn-lg btn-secondary blog-page__load-more"
						visible={!loading}
					/>
				</div>
			</div>

			<ModalVideo
				modalVisible={modalVisible}
				setModalVisible={setModalVisible}
				videoSrc={videoSrc}
				getContainer={`.article-video-${videoIndex}`}
			/>
		</>
	);
};

const FRVideos: React.FC<VideosProps> = ({ searchQuery }) => {
	return (
		<HelmetProvider>
			<CustomRequestProvider version="v2">
				<ItemsProvider
					type="contentTopics/videos/content"
					loadRequest="contentTopics/videos/content"
					pagination={{ pageSize: 10, total: 0 }}
				>
					<Videos searchQuery={searchQuery} />
				</ItemsProvider>
			</CustomRequestProvider>
		</HelmetProvider>
	);
};

export default FRVideos;
